import React, {FC, useCallback, useEffect, useState} from 'react';
import styled from 'styled-components';

import {AsyncStatusesEnum} from '../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {DataEventActionsEnum} from '../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {TopBarDataEventObject} from '../../../../../libs/shared/core/src/models/tracking/objectNames/topBarDataEventObject';
import {useDataEventTracking} from '../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {AppPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  Skeleton,
  SkeletonColorSchemesEnum,
} from '../../../../../libs/shared/ui/src/components/utility/skeleton';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {SearchHelpCenterResultModel} from '../../../../models/api/searchHelpCenterModel';
import {getCompany} from '../../../../store/data/company/companySelectors';
import {getHelpCenterSearchCache} from '../../../../store/data/localPreferences/localPreferencesSelectors';
import {HelpCenterSearch} from '../../../startGuide/helpCenterSearch/helpCenterSearch';
import {HelpCenterSearchResults} from '../../../startGuide/helpCenterSearch/helpCenterSearchResults';
import {HelpTopics} from './helpTopics';

/*
 * Props.
 */

interface SettingsHelpAndTipsWidgetProps {
  panelId: AppPanelId;
}

const StyledContainerDiv = styled.div`
  width: 600px;
  max-height: 598px;
  display: flex;
  flex-direction: column;
  padding: 12px 30px 30px 30px;
  gap: 8px;
`;

const StyledResultsContainerDiv = styled.div`
  overflow: scroll;
`;

const StyledSkeletonContainerDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

/*
 * Component.
 */

export const SettingsHelpAndTipsWidget: FC<SettingsHelpAndTipsWidgetProps> = ({panelId}) => {
  const initialSearchCache = useAppSelector(getHelpCenterSearchCache);
  const company = useAppSelector(getCompany);
  const {trackDataEvent} = useDataEventTracking();

  const [helpCenterSearchQuery, setHelpCenterSearchQuery] = useState(initialSearchCache?.query);
  const [helpCenterSearchStatus, setHelpCenterSearchStatus] = useState<AsyncStatusesEnum>();
  const [helpCenterSearchResults, setHelpCenterSearchResults] = useState(initialSearchCache?.result);

  useEffect(() => {
    trackDataEvent(DataEventActionsEnum.VIEW, TopBarDataEventObject.HELP_WIDGET_HELP_TAB);
  }, [trackDataEvent]);

  const onHelpCenterSearchResultsChange = useCallback(
    (status: AsyncStatusesEnum, result?: SearchHelpCenterResultModel) => {
      setHelpCenterSearchStatus(status);
      setHelpCenterSearchResults(result);
    },
    [],
  );

  return (
    <StyledContainerDiv>
      <HelpCenterSearch
        initialQuery={initialSearchCache?.query}
        onQueryChange={setHelpCenterSearchQuery}
        onResultsChange={onHelpCenterSearchResultsChange}
        company={company}
      />
      {helpCenterSearchQuery ? (
        <StyledResultsContainerDiv>
          {helpCenterSearchStatus === AsyncStatusesEnum.LOADING ? (
            <StyledSkeletonContainerDiv>
              <Skeleton height="16px" width="214px" scheme={SkeletonColorSchemesEnum.GREY30} />
              <Skeleton height="16px" width="540px" scheme={SkeletonColorSchemesEnum.GREY10} />
              <Skeleton height="16px" width="214px" scheme={SkeletonColorSchemesEnum.GREY30} />
              <Skeleton height="16px" width="540px" scheme={SkeletonColorSchemesEnum.GREY10} />
              <Skeleton height="16px" width="214px" scheme={SkeletonColorSchemesEnum.GREY30} />
              <Skeleton height="16px" width="540px" scheme={SkeletonColorSchemesEnum.GREY10} />
            </StyledSkeletonContainerDiv>
          ) : (
            <HelpCenterSearchResults
              query={helpCenterSearchQuery}
              status={helpCenterSearchStatus}
              results={helpCenterSearchResults}
            />
          )}
        </StyledResultsContainerDiv>
      ) : (
        <HelpTopics panelId={panelId} />
      )}
    </StyledContainerDiv>
  );
};
